import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, Typography, useMediaQuery,
    Chip, Divider, Tabs, Tab,
} from "@mui/material";
import {VpnKey, Public, ErrorOutline, QrCode2, Analytics, Palette, Dns} from "@mui/icons-material";
import {createContext, useContext, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import SideBar from "../Componant/sidebar/Sidebar";
import CodeBlock from "../Componant/CodeBlock";
import {buildSnippet, SNIPPET_LANGUAGES} from "../Utils/apiSnippets";

function apiOrigin() {
    return process.env.REACT_APP_API_URL || 'http://localhost:3033';
}

const LanguageContext = createContext('curl');

const METHOD_COLORS = {
    GET: '#2196f3',
    POST: '#4caf50',
    PUT: '#ff9800',
    DELETE: '#f44336',
};

function MethodChip({method}) {
    const color = METHOD_COLORS[method] || '#9e9e9e';
    return (
        <Chip
            size="small"
            label={method}
            sx={{
                backgroundColor: `${color}22`,
                color,
                fontWeight: 700,
                fontFamily: 'monospace',
                border: `1px solid ${color}55`,
                minWidth: 64,
            }}
        />
    );
}

function EndpointDoc({method, path, examplePath, description, params, body, query, binaryOutput, response}) {
    const language = useContext(LanguageContext);
    const origin = apiOrigin();

    const snippet = useMemo(() => buildSnippet(language, {
        method,
        url: origin + (examplePath || path),
        query,
        body,
        binaryOutput,
    }), [language, method, origin, examplePath, path, query, body, binaryOutput]);

    return (
        <Box sx={{pb: 3, mb: 3, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': {border: 0, mb: 0, pb: 0}}}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{mb: 1}}>
                <MethodChip method={method}/>
                <Typography component="code" sx={{fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all'}}>
                    {path}
                </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: params ? 2 : 1.5}}>
                {description}
            </Typography>

            {params && params.length > 0 && (
                <Box sx={{mb: 2, overflowX: 'auto'}}>
                    <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
                        <Box component="thead">
                            <Box component="tr">
                                {['Paramètre', 'Type', 'Description'].map((h) => (
                                    <Box component="th" key={h} sx={{
                                        textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider',
                                        color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap',
                                    }}>{h}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {params.map((p) => (
                                <Box component="tr" key={p.name}>
                                    <Box component="td" sx={{p: 1, borderBottom: '1px solid', borderColor: 'divider', fontFamily: 'monospace'}}>
                                        {p.name}{p.required && <Box component="span" sx={{color: 'error.main'}}> *</Box>}
                                    </Box>
                                    <Box component="td" sx={{p: 1, borderBottom: '1px solid', borderColor: 'divider', color: 'text.secondary', whiteSpace: 'nowrap'}}>
                                        {p.type}
                                    </Box>
                                    <Box component="td" sx={{p: 1, borderBottom: '1px solid', borderColor: 'divider'}}>
                                        {p.description}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            )}

            <CodeBlock sx={{mb: response ? 1.5 : 0}} language={language}>{snippet}</CodeBlock>

            {response && (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 0.5}}>
                        Réponse
                    </Typography>
                    <CodeBlock language="json">{response}</CodeBlock>
                </>
            )}
        </Box>
    );
}

function DocSection({id, icon, title, intro, children}) {
    return (
        <Box id={id} sx={{scrollMarginTop: 90, mb: 5}}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{mb: 1.5}}>
                {icon}
                <Typography variant="h5" fontWeight={600}>{title}</Typography>
            </Stack>
            {intro && (
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
                    {intro}
                </Typography>
            )}
            <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2, p: {xs: 2, sm: 3}}}>
                {children}
            </Box>
        </Box>
    );
}

const SECTIONS = [
    {id: 'auth', label: 'Authentification', icon: <VpnKey fontSize="small"/>},
    {id: 'generation', label: 'Génération de QR codes', icon: <QrCode2 fontSize="small"/>},
    {id: 'qrcodes', label: 'QR codes trackés', icon: <QrCode2 fontSize="small"/>},
    {id: 'analytics', label: 'Analytics', icon: <Analytics fontSize="small"/>},
    {id: 'palettes', label: 'Palettes', icon: <Palette fontSize="small"/>},
    {id: 'domains', label: 'Domaines', icon: <Dns fontSize="small"/>},
    {id: 'errors', label: "Codes d'erreur", icon: <ErrorOutline fontSize="small"/>},
];

const STYLE_PARAMS_NOTE = 'width, height, dotColor, dotType, dotGradient*, backgroundColor, backgroundRound, ' +
    'backgroundGradient*, cornerSquareType, cornerSquareColor, cornerDotType, cornerDotColor, logo, logoSize, errorCorrectionLevel';

function ApiDocsPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();
    const [language, setLanguage] = useState('curl');
    const navigate = useNavigate();
    const origin = apiOrigin();

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({behavior: 'smooth'});
    };

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: {xs: 2, sm: 4}, margin: '0 auto', width: '100%', maxWidth: 1400, textAlign: 'left'}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="space-between" alignItems={{xs: 'stretch', sm: 'center'}} mb={1}>
                            <Typography variant="h4">Documentation API</Typography>
                            <Button variant="contained" color="primary" startIcon={<VpnKey/>} onClick={() => navigate('/api-keys')}>
                                Clés API
                            </Button>
                        </Stack>
                        <Typography color="text.secondary" sx={{mb: 3}}>
                            Base URL : <Box component="code" sx={{fontFamily: 'monospace'}}>{origin}</Box>
                        </Typography>

                        <Box sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 4}}>
                            <Tabs
                                value={language}
                                onChange={(e, value) => setLanguage(value)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{minHeight: 44, '& .MuiTab-root': {minHeight: 44, textTransform: 'none'}}}
                            >
                                {SNIPPET_LANGUAGES.map((l) => (
                                    <Tab key={l.id} value={l.id} label={l.label}/>
                                ))}
                            </Tabs>
                        </Box>

                        {!isComputer && (
                            <Stack direction="row" spacing={1} sx={{mb: 4, overflowX: 'auto', pb: 1}}>
                                {SECTIONS.map((s) => (
                                    <Chip key={s.id} label={s.label} icon={s.icon} onClick={() => scrollTo(s.id)} sx={{flexShrink: 0}}/>
                                ))}
                            </Stack>
                        )}

                        <LanguageContext.Provider value={language}>
                            <Stack direction="row" spacing={5} alignItems="flex-start">
                                {isComputer && (
                                    <Box sx={{width: 240, flexShrink: 0, position: 'sticky', top: 24}}>
                                        <Stack spacing={0.5}>
                                            {SECTIONS.map((s) => (
                                                <Button
                                                    key={s.id}
                                                    onClick={() => scrollTo(s.id)}
                                                    startIcon={s.icon}
                                                    sx={{justifyContent: 'flex-start', color: 'text.secondary', textTransform: 'none'}}
                                                >
                                                    {s.label}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <DocSection
                                        id="auth" icon={<VpnKey color="primary"/>} title="Authentification"
                                        intro="Toutes les requêtes (hors redirection de scan) nécessitent une clé API dans l'en-tête X-API-Key. Générez-en une depuis la page Clés API."
                                    >
                                        <CodeBlock sx={{mb: 2}}>{`X-API-Key: qrsk_votre_cle_ici`}</CodeBlock>
                                        <Stack direction="row" spacing={1} alignItems="flex-start">
                                            <Public fontSize="small" sx={{mt: 0.3, color: 'text.secondary'}}/>
                                            <Typography variant="body2" color="text.secondary">
                                                Si la clé est restreinte à des origines, la requête doit provenir d'une
                                                de ces URLs (en-tête <code>Origin</code> ou <code>Referer</code>) —
                                                sinon la réponse est <code>403 Forbidden</code>.
                                            </Typography>
                                        </Stack>
                                    </DocSection>

                                    <DocSection
                                        id="generation" icon={<QrCode2 color="primary"/>} title="Génération de QR codes"
                                        intro="Génération à la volée, sans sauvegarde ni suivi."
                                    >
                                        <EndpointDoc
                                            method="POST" path="/api/qrcodes/generate"
                                            description="Génère un QR code avec style complet, retourné en base64 (data URL)."
                                            params={[
                                                {name: 'url', type: 'string', required: true, description: 'Contenu à encoder'},
                                                {name: 'format', type: 'string', description: "png | jpeg | svg (défaut png)"},
                                                {name: 'style…', type: 'various', description: STYLE_PARAMS_NOTE},
                                            ]}
                                            body={{url: 'https://exemple.com', format: 'png', dotColor: '#FF7233'}}
                                            response={`{
  "success": true,
  "image": "data:image/png;base64,...",
  "format": "png"
}`}
                                        />
                                        <EndpointDoc
                                            method="GET" path="/api/qrcodes/generate"
                                            description="Même génération, via query string, retourne directement le fichier image (pratique pour un script)."
                                            params={[
                                                {name: 'url', type: 'string', required: true, description: 'Contenu à encoder'},
                                                {name: 'format', type: 'string', description: "png | jpeg | svg"},
                                            ]}
                                            query={{url: 'https://exemple.com', format: 'png'}}
                                            binaryOutput="qrcode.png"
                                        />
                                    </DocSection>

                                    <DocSection
                                        id="qrcodes" icon={<QrCode2 color="primary"/>} title="QR codes trackés"
                                        intro="QR codes sauvegardés, avec suivi optionnel des scans (redirection via un short code)."
                                    >
                                        <EndpointDoc
                                            method="POST" path="/api/qrcodes"
                                            description="Crée un QR code."
                                            params={[
                                                {name: 'name', type: 'string', required: true, description: 'Nom interne'},
                                                {name: 'targetUrl', type: 'string', required: true, description: 'URL cible (http/https)'},
                                                {name: 'trackingEnabled', type: 'boolean', description: 'Défaut true'},
                                                {name: 'domainId', type: 'string', description: 'Domaine personnalisé associé'},
                                                {name: 'expiresAt', type: 'ISO date', description: "QR éphémère : date au-delà de laquelle la redirection est désactivée"},
                                                {name: 'maxScans', type: 'number', description: 'QR éphémère : nombre de scans max avant désactivation'},
                                                {name: 'style…', type: 'various', description: STYLE_PARAMS_NOTE},
                                            ]}
                                            body={{name: 'Flyer salon', targetUrl: 'https://exemple.com'}}
                                            response={`{
  "success": true,
  "qrcode": { "id": "12", "name": "Flyer salon", "shortCode": "aZ3kLm2Q", ... },
  "image": "data:image/png;base64,..."
}`}
                                        />
                                        <EndpointDoc method="GET" path="/api/qrcodes" description="Liste tous vos QR codes."/>
                                        <EndpointDoc method="GET" path="/api/qrcodes/:id" examplePath="/api/qrcodes/12" description="Détail d'un QR code."/>
                                        <EndpointDoc method="PUT" path="/api/qrcodes/:id" examplePath="/api/qrcodes/12"
                                            description="Met à jour un QR code (mêmes champs que la création, tous optionnels)."
                                            body={{isActive: false}}/>
                                        <EndpointDoc method="DELETE" path="/api/qrcodes/:id" examplePath="/api/qrcodes/12" description="Supprime un QR code."/>
                                        <EndpointDoc method="GET" path="/api/qrcodes/:id/image" examplePath="/api/qrcodes/12/image"
                                            description="Régénère l'image (utile après un changement de style)."
                                            params={[{name: 'format', type: 'string', description: 'png | jpeg | svg'}]}
                                            query={{format: 'svg'}}/>
                                    </DocSection>

                                    <DocSection
                                        id="analytics" icon={<Analytics color="primary"/>} title="Analytics"
                                        intro="Statistiques de scan d'un QR code tracké."
                                    >
                                        <EndpointDoc
                                            method="GET" path="/api/qrcodes/:id/analytics" examplePath="/api/qrcodes/12/analytics"
                                            description="Résumé + répartitions (appareils, navigateurs, OS, pays, langues)."
                                            params={[
                                                {name: 'from', type: 'ISO date', description: 'Début de période (optionnel)'},
                                                {name: 'to', type: 'ISO date', description: 'Fin de période (optionnel)'},
                                            ]}
                                            query={{from: '2026-07-01'}}/>
                                        <EndpointDoc
                                            method="GET" path="/api/qrcodes/:id/analytics/activity" examplePath="/api/qrcodes/12/analytics/activity"
                                            description="Courbe d'activité à granularité adaptative."
                                            params={[{name: 'period', type: 'string', description: 'today | 7d | month | year'}]}
                                            query={{period: '7d'}}/>
                                        <EndpointDoc
                                            method="GET" path="/api/qrcodes/:id/analytics/cities" examplePath="/api/qrcodes/12/analytics/cities"
                                            description="Villes les plus scannées pour un pays donné."
                                            params={[{name: 'country', type: 'string', required: true, description: "Code pays ISO 2 lettres, ex. FR"}]}
                                            query={{country: 'FR'}}/>
                                    </DocSection>

                                    <DocSection
                                        id="palettes" icon={<Palette color="primary"/>} title="Palettes"
                                        intro="Styles réutilisables (couleurs, formes, logo)."
                                    >
                                        <EndpointDoc method="POST" path="/api/palettes" description="Crée une palette."
                                            params={[
                                                {name: 'name', type: 'string', required: true, description: 'Nom'},
                                                {name: 'style', type: 'object', required: true, description: STYLE_PARAMS_NOTE},
                                            ]}
                                            body={{name: 'Charte', style: {dotColor: '#FF7233'}}}/>
                                        <EndpointDoc method="GET" path="/api/palettes" description="Liste vos palettes."/>
                                        <EndpointDoc method="GET" path="/api/palettes/:id" examplePath="/api/palettes/4" description="Détail d'une palette."/>
                                        <EndpointDoc method="PUT" path="/api/palettes/:id" examplePath="/api/palettes/4" description="Met à jour une palette."
                                            body={{name: 'Charte 2026'}}/>
                                        <EndpointDoc method="DELETE" path="/api/palettes/:id" examplePath="/api/palettes/4" description="Supprime une palette."/>
                                    </DocSection>

                                    <DocSection
                                        id="domains" icon={<Dns color="primary"/>} title="Domaines"
                                        intro="Domaines personnalisés pour les liens de redirection trackés."
                                    >
                                        <EndpointDoc method="POST" path="/api/domains" description="Enregistre un domaine (CNAME requis, voir la page Domaines)."
                                            params={[{name: 'domain', type: 'string', required: true, description: 'Ex. qr.mon-domaine.fr'}]}
                                            body={{domain: 'qr.mon-domaine.fr'}}/>
                                        <EndpointDoc method="GET" path="/api/domains" description="Liste vos domaines."/>
                                        <EndpointDoc method="DELETE" path="/api/domains/:id" examplePath="/api/domains/7" description="Supprime un domaine."/>
                                    </DocSection>

                                    <DocSection id="errors" icon={<ErrorOutline color="primary"/>} title="Codes d'erreur">
                                        <Box sx={{overflowX: 'auto'}}>
                                            <Box component="table" sx={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
                                                <Box component="thead">
                                                    <Box component="tr">
                                                        {['Code', 'Signification'].map((h) => (
                                                            <Box component="th" key={h} sx={{
                                                                textAlign: 'left', p: 1, borderBottom: '1px solid', borderColor: 'divider',
                                                                color: 'text.secondary', fontWeight: 600,
                                                            }}>{h}</Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                                <Box component="tbody">
                                                    {[
                                                        ['400', "Requête invalide (champ manquant ou mal formé, ex. targetUrl non-http)"],
                                                        ['401', "Clé API manquante, invalide ou révoquée"],
                                                        ['403', "Origine non autorisée pour cette clé API"],
                                                        ['404', "Ressource introuvable ou n'appartenant pas à votre compte"],
                                                        ['413', "Logo trop volumineux"],
                                                        ['500', "Erreur serveur"],
                                                    ].map(([code, label]) => (
                                                        <Box component="tr" key={code}>
                                                            <Box component="td" sx={{p: 1, borderBottom: '1px solid', borderColor: 'divider', fontFamily: 'monospace'}}>{code}</Box>
                                                            <Box component="td" sx={{p: 1, borderBottom: '1px solid', borderColor: 'divider'}}>{label}</Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </DocSection>

                                    <Divider sx={{my: 4}}/>
                                    <Typography variant="body2" color="text.secondary">
                                        Toutes les réponses sont au format JSON avec un champ <code>success</code>,
                                        sauf les téléchargements binaires (image directe, export CSV).
                                    </Typography>
                                </Box>
                            </Stack>
                        </LanguageContext.Provider>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default ApiDocsPage;
